import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  getRun,
  isActiveRunStatus,
  listRuns,
  smartMarketplaceErrorMessage,
  startRun,
  type AgentRunSummary,
  type AgentTrace,
  type SmartMarketplaceCandidate,
  type SmartMarketplaceDecision,
  type SmartMarketplaceRead,
  type SmartMarketplaceRecommendation,
  type SmartMarketplaceRun,
  type SmartMarketplaceTask,
  type SpecialistOutput,
  type ToolCallRecord,
} from '@/api/smartMarketplace';
import { sapyonAgentDeniedPath } from '@/components/layout/sapyonAgentNav';
import { AnalysisTableList, analysisSections } from '@/components/sapyonAgent/analysisTables';
import { ClientSettingsForm } from '@/components/sapyonAgent/BrandClientSettingsForm';
import {
  prosePoints,
  rationaleBullets,
  recommendationMeta,
  recommendationPoints,
  sortRecommendations,
} from '@/components/sapyonAgent/decisionPoints';
import { cn } from '@/utils/cn';

const AGENT_ORDER = ['brands', 'keyword', 'search_term', 'placement', 'asin', 'targeting'];

const AGENT_LABELS: Record<string, string> = {
  brands: 'Brands',
  keyword: 'Keyword',
  search_term: 'Search term',
  placement: 'Placement',
  asin: 'ASIN',
  targeting: 'Targeting',
  summary_writer: 'Summary writer',
};

const TASK_OPTIONS: { value: SmartMarketplaceTask; label: string }[] = [
  { value: 'optimize', label: 'All' },
  { value: 'brands', label: 'Brands' },
  { value: 'keyword', label: 'Keyword' },
  { value: 'search_term', label: 'Search term' },
  { value: 'placement', label: 'Placement' },
  { value: 'asin', label: 'ASIN' },
  { value: 'targeting', label: 'Targeting' },
];

function agentLabel(name: string): string {
  return AGENT_LABELS[name] ?? name;
}

function taskLabel(task: string): string {
  return TASK_OPTIONS.find((option) => option.value === task)?.label ?? labelize(task);
}

function orderedAgents(outputs: SpecialistOutput[]): SpecialistOutput[] {
  return [...outputs].sort((left, right) => {
    const leftIndex = AGENT_ORDER.indexOf(left.agent_name);
    const rightIndex = AGENT_ORDER.indexOf(right.agent_name);
    const leftRank = leftIndex === -1 ? AGENT_ORDER.length : leftIndex;
    const rightRank = rightIndex === -1 ? AGENT_ORDER.length : rightIndex;
    return leftRank - rightRank;
  });
}

function labelize(value: string | null | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

function text(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function statusVariant(status: string | null | undefined): BadgeVariant {
  if (status === 'completed' || status === 'passed' || status === 'eligible') return 'success';
  if (status === 'failed' || status === 'blocked') return 'danger';
  if (
    status === 'queued' ||
    status === 'running' ||
    status === 'partial' ||
    status === 'needs_input' ||
    status === 'needs_review'
  ) {
    return 'warning';
  }
  if (status === 'insufficient_evidence') return 'archived';
  return 'neutral';
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function retryLabel(count: number | undefined): string {
  const value = count ?? 0;
  return value === 1 ? '1 retry' : `${value} retries`;
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  return (
    <Badge variant={statusVariant(status)} className="normal-case tracking-normal">
      {labelize(status)}
    </Badge>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-border bg-surface p-4">
      <h2 className="text-body-md font-semibold text-brand-ink">{title}</h2>
      {children}
    </section>
  );
}

const SAMPLE_SIZE_PREFIX = 'Rows below the sample-size policy:';

function sampleSizeIds(item: string): string[] | null {
  if (!item.startsWith(SAMPLE_SIZE_PREFIX)) return null;
  return item
    .slice(SAMPLE_SIZE_PREFIX.length)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function BulletList({ items }: { items: string[] | undefined }) {
  if (!items?.length) {
    return <p className="text-body-sm text-foreground-muted">None</p>;
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-body-sm text-foreground">
      {items.map((item, index) => {
        const ids = sampleSizeIds(item);
        if (ids) {
          return (
            <li key={`sample-size-${index}`}>
              <details>
                <summary className="cursor-pointer">
                  Rows below the sample-size policy ({ids.length} rows)
                </summary>
                <div className="custom-scrollbar mt-1 max-h-40 overflow-auto break-all">
                  {ids.join(', ')}
                </div>
              </details>
            </li>
          );
        }
        return <li key={`${item}-${index}`}>{item}</li>;
      })}
    </ul>
  );
}

function LabeledList({ title, items }: { title: string; items: string[] | undefined }) {
  return (
    <div className="space-y-1">
      <h3 className="text-body-sm font-medium text-foreground-muted">{title}</h3>
      <BulletList items={items} />
    </div>
  );
}

function RecommendationTable({
  rows,
  empty,
}: {
  rows: SmartMarketplaceRecommendation[];
  empty: string;
}) {
  if (rows.length === 0) {
    return <p className="text-body-sm text-foreground-muted">{empty}</p>;
  }
  return (
    <TableWrap>
      <Table compact>
        <TableHeader>
          <TableRow>
            <TableHead>Rank</TableHead>
            <TableHead>Agent</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Current</TableHead>
            <TableHead>Proposed</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Rationale</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {rows.map((row) => (
            <TableRow key={row.recommendation_id}>
              <TableCell>{row.rank ?? '—'}</TableCell>
              <TableCell>{row.agent_name ? agentLabel(row.agent_name) : '—'}</TableCell>
              <TableCell>{labelize(row.action_type)}</TableCell>
              <TableCell>{text(row.entity_scope)}</TableCell>
              <TableCell>{text(row.current_value)}</TableCell>
              <TableCell>{text(row.proposed_value)}</TableCell>
              <TableCell>
                <StatusBadge status={row.status} />
              </TableCell>
              <TableCell className="min-w-64">
                <p>{row.rationale}</p>
                {row.guardrail_reasons?.length ? (
                  <p className="mt-1 text-body-sm text-foreground-muted">{row.guardrail_reasons.join(' ')}</p>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

function CandidateTable({ rows }: { rows: SmartMarketplaceCandidate[] }) {
  if (rows.length === 0) {
    return <p className="text-body-sm text-foreground-muted">None</p>;
  }
  return (
    <TableWrap>
      <Table compact>
        <TableHeader>
          <TableRow>
            <TableHead>Action</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Current</TableHead>
            <TableHead>Proposed</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead>Risk</TableHead>
            <TableHead>Rationale</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {rows.map((row) => (
            <TableRow key={row.recommendation_id}>
              <TableCell>{labelize(row.action_type)}</TableCell>
              <TableCell>{text(row.entity_scope)}</TableCell>
              <TableCell>{text(row.current_value)}</TableCell>
              <TableCell>{text(row.proposed_value)}</TableCell>
              <TableCell>{labelize(row.confidence)}</TableCell>
              <TableCell>{labelize(row.risk_level)}</TableCell>
              <TableCell className="min-w-64">{row.rationale}</TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

function jsonText(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function JsonPre({ value }: { value: unknown }) {
  return (
    <pre className="custom-scrollbar max-h-80 overflow-auto whitespace-pre-wrap break-all text-xs text-foreground">
      {jsonText(value)}
    </pre>
  );
}

function usageLabel(usage: AgentTrace['usage']): string | null {
  if (!usage) return null;
  const input = usage.input_tokens;
  const output = usage.output_tokens;
  if (typeof input !== 'number' && typeof output !== 'number') return null;
  const inputText = typeof input === 'number' ? String(input) : '—';
  const outputText = typeof output === 'number' ? String(output) : '—';
  return `${inputText} in / ${outputText} out`;
}

function TranscriptView({
  trace,
  stored,
}: {
  trace: AgentTrace | undefined;
  stored: boolean;
}) {
  if (!stored || !trace) {
    return (
      <p className="text-body-sm text-foreground-muted">The transcript was not stored for this run.</p>
    );
  }
  const calls = trace.tool_calls ?? [];
  const failedAttempts = (trace.attempts ?? []).filter((attempt) => attempt.error);
  const meta = [trace.model_name, usageLabel(trace.usage)].filter(Boolean).join(' · ');
  return (
    <div className="space-y-3">
      <h4 className="text-body-sm font-medium text-foreground-muted">Transcript</h4>
      {meta ? <p className="text-body-sm text-foreground-muted">{meta}</p> : null}
      {trace.error ? <p className="text-body-sm text-error-ink">{trace.error}</p> : null}
      {failedAttempts.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-body-sm text-foreground">
          {(trace.attempts ?? []).map((attempt, index) => (
            <li key={`attempt-${index}`}>
              Attempt {index + 1}: {attempt.error ?? 'completed'}
            </li>
          ))}
        </ul>
      ) : null}
      <details className="rounded-lg border border-border bg-surface" open>
        <summary className="cursor-pointer px-3 py-2 text-body-sm font-medium text-brand-ink">Input</summary>
        <div className="px-3 pb-3">
          <JsonPre value={trace.user_prompt || '—'} />
        </div>
      </details>
      <details className="rounded-lg border border-border bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-body-sm font-medium text-brand-ink">
          Instructions
        </summary>
        <div className="px-3 pb-3">
          <JsonPre value={trace.instructions || '—'} />
        </div>
      </details>
      <div className="space-y-2">
        <h4 className="text-body-sm font-medium text-foreground-muted">Tool calls</h4>
        {calls.length === 0 ? (
          <p className="text-body-sm text-foreground-muted">None</p>
        ) : (
          calls.map((call: ToolCallRecord, index) => (
            <article
              key={`${call.tool_call_id}-${index}`}
              className="space-y-2 rounded-lg border border-border bg-surface p-3"
            >
              <h5 className="text-body-sm font-medium text-brand-ink">{call.tool_name}</h5>
              <div className="space-y-1">
                <p className="text-body-sm text-foreground-muted">Input</p>
                <JsonPre value={call.args ?? null} />
              </div>
              <div className="space-y-1">
                <p className="text-body-sm text-foreground-muted">Output</p>
                <JsonPre value={call.result ?? null} />
              </div>
            </article>
          ))
        )}
      </div>
      <details className="rounded-lg border border-border bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-body-sm font-medium text-brand-ink">Output</summary>
        <div className="px-3 pb-3">
          <JsonPre value={trace.output ?? null} />
        </div>
      </details>
      <details className="rounded-lg border border-border bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-body-sm font-medium text-brand-ink">
          Messages
        </summary>
        <div className="px-3 pb-3">
          <JsonPre value={trace.messages ?? []} />
        </div>
      </details>
    </div>
  );
}

function AnalysisBlock({ outputs }: { outputs: SpecialistOutput[] }) {
  const sections = analysisSections(orderedAgents(outputs));
  if (sections.length === 0) return null;
  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.agentName} className="space-y-3">
          <h3 className="text-body-sm font-semibold text-brand-ink">{agentLabel(section.agentName)}</h3>
          <AnalysisTableList tables={section.tables} />
        </div>
      ))}
    </div>
  );
}

function AgentPanel({
  output,
  summary,
  trace,
  stored,
}: {
  output: SpecialistOutput;
  summary: AgentRunSummary | undefined;
  trace: AgentTrace | undefined;
  stored: boolean;
}) {
  return (
    <article className="space-y-4 rounded-xl border border-border bg-page p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-body-md font-semibold text-brand-ink">{agentLabel(output.agent_name)}</h3>
        <StatusBadge status={output.status} />
        <span className="text-body-sm text-foreground-muted">
          {formatDuration(summary?.duration_ms)} · {retryLabel(summary?.retry_count)}
        </span>
      </div>
      {output.error || summary?.error ? (
        <p className="text-body-sm text-error-ink">{output.error || summary?.error}</p>
      ) : null}
      <div className="space-y-2">
        <h4 className="text-body-sm font-medium text-foreground-muted">Findings</h4>
        {output.findings?.length ? (
          <ul className="space-y-2">
            {output.findings.map((finding, index) => (
              <li key={`${finding.summary}-${index}`} className="text-body-sm text-foreground">
                <span>{finding.summary}</span>
                {finding.classification ? (
                  <span className="ml-2 text-foreground-muted">{labelize(finding.classification)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-body-sm text-foreground-muted">None</p>
        )}
      </div>
      <AnalysisTableList tables={output.tables} />
      <div className="space-y-2">
        <h4 className="text-body-sm font-medium text-foreground-muted">Candidates</h4>
        <CandidateTable rows={output.candidates ?? []} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <LabeledList title="Assumptions" items={output.assumptions} />
        <LabeledList title="Risks" items={output.risks} />
        <LabeledList title="Warnings" items={output.warnings} />
        <LabeledList title="Missing data" items={output.missing_data} />
      </div>
      <TranscriptView trace={trace} stored={stored} />
    </article>
  );
}

function ReadsBlock({ reads }: { reads: SmartMarketplaceRead[] }) {
  if (reads.length === 0) {
    return <p className="text-body-sm text-foreground-muted">No reads were stored for this run.</p>;
  }
  return (
    <div className="space-y-2">
      {reads.map((read, index) => (
        <details key={`${read.key}-${index}`} className="rounded-lg border border-border bg-page">
          <summary className="cursor-pointer px-3 py-2 text-body-sm font-medium text-brand-ink">
            {read.key}
            {read.error ? <span className="ml-2 font-normal text-error-ink">{read.error}</span> : null}
          </summary>
          <pre className="custom-scrollbar max-h-80 overflow-auto px-3 pb-3 text-xs text-foreground">
            {JSON.stringify(read, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function RecommendationModule({ row }: { row: SmartMarketplaceRecommendation }) {
  const heading = [row.rank != null ? `Rank ${row.rank}` : 'Unranked', labelize(row.action_type)].join(' · ');
  const meta = recommendationMeta(row);
  const points = recommendationPoints(row);
  return (
    <article className="space-y-2 rounded-lg border border-border bg-page p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-body-sm font-semibold text-brand-ink">{heading}</h3>
        <StatusBadge status={row.status} />
        {row.agent_name ? (
          <span className="text-body-sm text-foreground-muted">{agentLabel(row.agent_name)}</span>
        ) : null}
      </div>
      <p className="text-body-sm text-foreground">{text(row.entity_scope)}</p>
      {meta ? <p className="text-body-sm text-foreground-muted">{meta}</p> : null}
      {points.length > 0 ? <BulletList items={points} /> : null}
    </article>
  );
}

function SummaryLead({ points }: { points: string[] }) {
  if (points.length === 0 || points.length > 2) return null;
  return (
    <div className="space-y-1">
      {points.map((point, index) => (
        <p key={`${point}-${index}`} className="text-body-md text-foreground">
          {point}
        </p>
      ))}
    </div>
  );
}

function DecisionView({ decision, outputs, reads, traces }: {
  decision: SmartMarketplaceDecision;
  outputs: SpecialistOutput[];
  reads: SmartMarketplaceRead[];
  traces: AgentTrace[] | null;
}) {
  const summaries = new Map((decision.agent_run_summary ?? []).map((item) => [item.agent_name, item]));
  const stored = Array.isArray(traces);
  const byAgent = new Map((traces ?? []).map((item) => [item.agent_name, item]));
  const writer = byAgent.get('summary_writer');
  const summaryPoints = prosePoints(decision.summary);
  const recommendations = sortRecommendations(decision.recommendations);
  return (
    <div className="space-y-4">
      <Section title="Decision">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={decision.run_status} />
        </div>
        <SummaryLead points={summaryPoints} />
        <AnalysisBlock outputs={outputs} />
        {recommendations.length > 0 ? (
          <div className="space-y-3">
            {recommendations.map((row) => (
              <RecommendationModule key={row.recommendation_id} row={row} />
            ))}
          </div>
        ) : null}
        {summaryPoints.length > 2 ? <LabeledList title="Summary" items={summaryPoints} /> : null}
        <LabeledList title="Rationale" items={rationaleBullets(decision)} />
        <div className="grid gap-4 md:grid-cols-3">
          <LabeledList title="Warnings" items={decision.warnings} />
          <LabeledList title="Missing data" items={decision.missing_data} />
          <LabeledList title="Questions" items={decision.questions} />
        </div>
      </Section>
      <Section title="Recommendations">
        <RecommendationTable rows={decision.recommendations ?? []} empty="No recommendations." />
      </Section>
      <Section title="Blocked actions">
        <RecommendationTable rows={decision.blocked_actions ?? []} empty="No blocked actions." />
      </Section>
      <Section title="Conflicts">
        {decision.conflicts?.length ? (
          <ul className="space-y-3">
            {decision.conflicts.map((conflict) => (
              <li key={conflict.conflict_id} className="space-y-1 text-body-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={conflict.resolution} />
                  <span className="text-foreground-muted">{text(conflict.entity_scope)}</span>
                </div>
                <p className="text-foreground">{conflict.description}</p>
                {conflict.resolution_reason ? (
                  <p className="text-foreground-muted">{conflict.resolution_reason}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-body-sm text-foreground-muted">No conflicts.</p>
        )}
      </Section>
      <Section title="Agents">
        {outputs.length === 0 ? (
          <p className="text-body-sm text-foreground-muted">No specialist output was stored for this run.</p>
        ) : (
          <div className="space-y-3">
            {orderedAgents(outputs).map((output) => (
              <AgentPanel
                key={output.agent_name}
                output={output}
                summary={summaries.get(output.agent_name)}
                trace={byAgent.get(output.agent_name)}
                stored={stored}
              />
            ))}
          </div>
        )}
      </Section>
      {writer ? (
        <Section title="Summary writer">
          <TranscriptView trace={writer} stored />
        </Section>
      ) : null}
      <Section title="Reads">
        <ReadsBlock reads={reads} />
      </Section>
    </div>
  );
}

function RunDetail({ run, isLoading }: { run: SmartMarketplaceRun | undefined; isLoading: boolean }) {
  if (isLoading && !run) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-foreground-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading run
      </div>
    );
  }
  if (!run) {
    return (
      <p className="text-body-md text-foreground-muted">
        No runs yet. Start one to see the decision and each agent&apos;s output.
      </p>
    );
  }
  const active = isActiveRunStatus(run.status);
  return (
    <div className="space-y-4" aria-busy={active}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={run.status} />
        {run.environment ? <StatusBadge status={run.environment} /> : null}
        {run.run_status ? <StatusBadge status={run.run_status} /> : null}
        {active ? <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" aria-hidden /> : null}
        <span className="text-body-sm text-foreground-muted">
          {formatWhen(run.created_at)}
          {run.finished_at ? ` – ${formatWhen(run.finished_at)}` : ''}
        </span>
      </div>
      <p className="text-body-sm text-foreground-muted">
        {taskLabel(run.task_type)} · {labelize(run.execution_mode)} · {run.model || '—'}
        {run.marketplace ? ` · ${run.marketplace}` : ''}
      </p>
      {run.status === 'failed' && run.error ? (
        <p className="rounded-lg border border-error/20 bg-error-tint px-3 py-2 text-body-sm text-error-ink">
          {run.error}
        </p>
      ) : null}
      {active ? (
        <p className="text-body-sm text-foreground-muted">
          This run is still going. The decision and each agent&apos;s output appear when it finishes.
        </p>
      ) : null}
      {run.decision_json ? (
        <DecisionView
          decision={run.decision_json}
          outputs={run.agents_json ?? []}
          reads={run.reads_json ?? []}
          traces={run.traces_json ?? null}
        />
      ) : null}
    </div>
  );
}

export function SapyonAgentPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { isSuperAdmin, loading } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [taskType, setTaskType] = useState<SmartMarketplaceTask>('optimize');

  useEffect(() => {
    setSelectedId(null);
  }, [profileId]);

  const listQuery = useQuery({
    queryKey: ['smart-marketplace-runs', profileId],
    queryFn: () => listRuns(profileId),
    enabled: Boolean(profileId) && isSuperAdmin && !loading,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs ?? [];
      return runs.some((run) => isActiveRunStatus(run.status)) ? 2000 : false;
    },
  });

  const runs = listQuery.data?.runs ?? [];
  const openId = selectedId ?? runs[0]?.id ?? null;

  const detailQuery = useQuery({
    queryKey: ['smart-marketplace-run', profileId, openId],
    queryFn: () => getRun(profileId, openId as string),
    enabled: Boolean(profileId && openId) && isSuperAdmin && !loading,
    refetchInterval: (query) => (isActiveRunStatus(query.state.data?.status) ? 2000 : false),
  });

  const start = useMutation({
    mutationFn: () => startRun(profileId, taskType),
    onSuccess: (run) => {
      setSelectedId(run.id);
      void queryClient.invalidateQueries({ queryKey: ['smart-marketplace-runs', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['smart-marketplace-run', profileId, run.id] });
    },
    onError: (error) => {
      showToast(smartMarketplaceErrorMessage(error, 'Could not start the run.'), 'error');
    },
  });

  if (loading) return null;
  if (!isSuperAdmin) {
    return <Navigate to={sapyonAgentDeniedPath(profileId)} replace />;
  }

  const hasActive =
    start.isPending ||
    runs.some((run) => isActiveRunStatus(run.status)) ||
    isActiveRunStatus(detailQuery.data?.status);
  const listError = listQuery.error
    ? smartMarketplaceErrorMessage(listQuery.error, 'Could not load runs.')
    : null;
  const detailError = detailQuery.error
    ? smartMarketplaceErrorMessage(detailQuery.error, 'Could not load this run.')
    : null;

  return (
    <div className="mx-auto min-h-full max-w-7xl space-y-5 bg-page px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-headline-md font-semibold text-foreground">Sapyon Agent</h1>
          <p className="mt-1 max-w-2xl text-body-sm text-foreground-muted">
            Start one recommendation-only optimization for this profile. The result includes the
            final decision and each specialist&apos;s findings.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Agent"
            value={taskType}
            disabled={!profileId || hasActive}
            onChange={(event) => setTaskType(event.target.value as SmartMarketplaceTask)}
          >
            {TASK_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button onClick={() => start.mutate()} disabled={!profileId || hasActive}>
            {start.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Play className="h-4 w-4" aria-hidden />
            )}
            Run
          </Button>
        </div>
      </div>

      {listError ? (
        <p className="rounded-lg border border-error/20 bg-error-tint px-3 py-2 text-body-sm text-error-ink">
          {listError}
        </p>
      ) : null}

      <ClientSettingsForm key={profileId} profileId={profileId} />

      <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-2">
          <h2 className="text-body-sm font-medium text-foreground-muted">Runs</h2>
          {listQuery.isLoading ? (
            <p className="text-body-sm text-foreground-muted">Loading runs</p>
          ) : null}
          {runs.length === 0 && !listQuery.isLoading ? (
            <p className="text-body-sm text-foreground-muted">No runs yet.</p>
          ) : null}
          <ul className="space-y-2">
            {runs.map((run) => {
              const selected = run.id === openId;
              return (
                <li key={run.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(run.id)}
                    aria-current={selected ? 'true' : undefined}
                    className={cn(
                      'w-full rounded-xl border px-3 py-2.5 text-left',
                      selected
                        ? 'border-brand bg-brand-subtle'
                        : 'border-border bg-surface hover:border-brand'
                    )}
                  >
                    <span className="block text-body-sm font-medium text-foreground">
                      {formatWhen(run.created_at)}
                    </span>
                    <span className="mt-0.5 block text-body-sm text-foreground-muted">
                      {taskLabel(run.task_type)}
                    </span>
                    <span className="mt-1.5 flex flex-wrap gap-1.5">
                      <StatusBadge status={run.status} />
                      {run.environment ? <StatusBadge status={run.environment} /> : null}
                      {run.run_status ? <StatusBadge status={run.run_status} /> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        <div className="min-w-0 space-y-3">
          {detailError ? (
            <p className="rounded-lg border border-error/20 bg-error-tint px-3 py-2 text-body-sm text-error-ink">
              {detailError}
            </p>
          ) : null}
          <RunDetail run={detailQuery.data} isLoading={detailQuery.isLoading && Boolean(openId)} />
        </div>
      </div>
    </div>
  );
}
